"use client"

import { useState, useEffect } from "react"
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom"
import {
  FiHome,
  FiTruck,
  FiFileText,
  FiUser,
  FiSettings,
  FiLogOut,
  FiPlus,
  FiMessageSquare,
  FiBell,
  FiMenu,
  FiX,
  FiChevronRight,
  FiChevronLeft,
  FiSearch,
  FiTrendingUp,
  FiCalendar,
  FiClock,
  FiZap,
  FiTarget,
  FiBarChart2,
  FiActivity,
  FiGlobe,
  FiShield,
  FiPackage,
} from "react-icons/fi"
import { motion, AnimatePresence } from "framer-motion"
import NotificationCenter from "./NotificationCenter"
import { useNotifications } from "../../contexts/NotificationContext"

const navItems = [
  { icon: FiHome, label: "Tableau de bord", path: "/", color: "text-amber-400" },
  { icon: FiSearch, label: " Recherche", path: "/client/search", color: "text-white-400" },
  { icon: FiTruck, label: "Déménagements", path: "/client/moves", color: "text-blue-400" },
  { icon: FiFileText, label: "Devis", path: "/client/quotes", color: "text-green-400" },
  { icon: FiMessageSquare, label: "Messages", path: "/client/messages", color: "text-purple-400" },
  { icon: FiUser, label: "Profil", path: "/client/profile", color: "text-pink-400" },
  { icon: FiSettings, label: "Tickets", path: "/client/tickets", color: "text-gray-400" },
]

const quickActions = [
  { 
    icon: FiPlus, 
    label: "Nouveau devis", 
    action: "quote", 
    color: "bg-gradient-to-r from-amber-500 to-yellow-600",
    description: "Créer un nouveau devis de déménagement"
  },
  { 
    icon: FiCalendar, 
    label: "Planifier", 
    action: "schedule", 
    color: "bg-gradient-to-r from-blue-500 to-blue-600",
    description: "Planifier un nouveau déménagement"
  },
  { 
    icon: FiZap, 
    label: "Urgence", 
    action: "urgent", 
    color: "bg-gradient-to-r from-red-500 to-red-600",
    description: "Signaler une urgence"
  },
  { 
    icon: FiTarget, 
    label: "Objectifs", 
    action: "goals", 
    color: "bg-gradient-to-r from-green-500 to-green-600",
    description: "Voir mes objectifs"
  },
]

const analyticsWidgets = [
  { icon: FiTrendingUp, label: "Revenus", value: "€12,450", change: "+12%", color: "text-amber-400" },
  { icon: FiBarChart2, label: "Devis", value: "23", change: "+5%", color: "text-blue-400" },
  { icon: FiActivity, label: "Activité", value: "89%", change: "+3%", color: "text-purple-400" },
  { icon: FiGlobe, label: "Portée", value: "156", change: "+8%", color: "text-amber-400" },
]

export default function ClientLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [showQuickActions, setShowQuickActions] = useState(false)
  const [showAnalytics, setShowAnalytics] = useState(true)

  const location = useLocation()
  const navigate = useNavigate()
  const { unreadCount } = useNotifications()

  const safeParseUser = () => {
    try {
      const rawUser = localStorage.getItem("user")
      if (!rawUser || rawUser === "undefined" || rawUser === "null") return {}
      const parsed = JSON.parse(rawUser)
      return parsed && typeof parsed === "object" ? parsed : {}
    } catch (error) {
      console.warn("[ClientLayout] Impossible de parser l'utilisateur localStorage:", error)
      return {}
    }
  }

  const user = safeParseUser()
  const firstName = user?.first_name || ""
  const lastName = user?.last_name || ""
  const userInitial = (firstName?.[0] || "U").toUpperCase()
  const userEmail = user?.email || "utilisateur@exemple.com"

  useEffect(() => {
    document.documentElement.classList.add("dark")
  }, [])

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      console.log("Searching for:", searchQuery)
      // Implement search logic here
    }
  }

  const handleQuickAction = (action) => {
    console.log("Quick action:", action)
    setShowQuickActions(false)
    
    // Gérer les différentes actions rapides
    switch (action) {
      case 'quote':
        navigate('/client/search')
        break
      case 'schedule':
        navigate('/client/search')
        break
      case 'urgent':
        // Créer un ticket urgent
        navigate('/client/tickets/new', { 
          state: { 
            urgent: true,
            type: 'reclamation',
            subject: 'Urgence - Assistance immédiate requise',
            priority: 'high'
          } 
        })
        break
      case 'goals':
        // Rediriger vers les objectifs
        navigate('/client/goals')
        break
      default:
        console.log('Action non gérée:', action)
    }
  }

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + "/")
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col md:flex-row">
      {/* Mobile header */}
      <header className="md:hidden relative z-40 bg-gray-800 shadow-sm border-b border-gray-700 overflow-visible">
        <div className="flex items-center justify-between h-16 px-4">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          >
            {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-amber-500 to-yellow-600 rounded-lg flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-sm">L</span>
            </div>
            <h1 className="text-xl font-bold text-amber-400">Liberty</h1>
          </div>
          <div className="relative flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 text-gray-400 rounded-lg hover:bg-gray-700 hover:text-white transition-colors"
            >
              <FiBell size={20} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-medium text-white bg-amber-500 rounded-full animate-pulse">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
            <AnimatePresence>
              {showNotifications && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  className="fixed inset-x-3 top-16 z-[100] max-h-[min(70vh,calc(100vh-5rem))] overflow-y-auto rounded-lg shadow-xl"
                >
                  <NotificationCenter onClose={() => setShowNotifications(false)} variant="dark" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      {/* Enhanced Sidebar */}
      <motion.aside
        initial={false}
        animate={{
          width: isSidebarOpen ? "20rem" : "5rem",
          opacity: 1,
        }}
        className={`hidden md:flex flex-col bg-gray-800 border-r border-gray-700 transition-all duration-300 ease-in-out ${
          isSidebarOpen ? "w-80" : "w-20"
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-700">
          {isSidebarOpen ? (
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-lg">L</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-amber-400">Liberty</h1>
                <p className="text-xs text-gray-400">Premium Moving</p>
              </div>
            </div>
          ) : (
            <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-lg">L</span>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
            >
              {isSidebarOpen ? <FiChevronLeft /> : <FiChevronRight />}
            </button>
          </div>
        </div>

        {isSidebarOpen && (
          <div className="p-4 border-b border-gray-700">
            <form onSubmit={handleSearch} className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors"
              />
            </form>
          </div>
        )}

        {isSidebarOpen && (
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-white">Actions rapides</h3>
              <button
                onClick={() => setShowQuickActions(!showQuickActions)}
                className="text-gray-400 hover:text-amber-400 transition-colors"
              >
                <FiZap size={16} />
              </button>
            </div>
            <AnimatePresence>
              {showQuickActions && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="grid grid-cols-2 gap-3 p-1"
                >
                  {quickActions.map((action, index) => (
                    <motion.button
                      key={action.action}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1, type: 'spring', stiffness: 300 }}
                      onClick={() => handleQuickAction(action.action)}
                      className={`p-3 rounded-xl ${action.color} text-white hover:opacity-90 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl flex flex-col items-center justify-center min-h-[80px]`}
                      title={action.description}
                    >
                      <action.icon size={18} className="mb-1.5" />
                      <span className="text-xs font-medium">{action.label}</span>
                    </motion.button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Enhanced Navigation */}
        <div className="flex-1 overflow-y-auto py-4">
          <nav className="space-y-2 px-3">
            {navItems.map((item, index) => {
              const Icon = item.icon
              const active = isActive(item.path)
              return (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={item.path}
                    className={`flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 group ${
                      active
                        ? "bg-gradient-to-r from-amber-500 to-yellow-600 text-white shadow-lg transform scale-105"
                        : "text-gray-300 hover:bg-gray-700 hover:text-white hover:transform hover:scale-102"
                    }`}
                  >
                    <Icon className={`flex-shrink-0 h-5 w-5 ${active ? "text-white" : item.color} transition-colors`} />
                    {isSidebarOpen && <span className="ml-3 transition-all duration-200">{item.label}</span>}
                    {active && isSidebarOpen && (
                      <motion.div layoutId="activeIndicator" className="ml-auto w-2 h-2 bg-white rounded-full" />
                    )}
                  </Link>
                </motion.div>
              )
            })}
          </nav>
        </div>


        {/* Enhanced User Section */}
        <div className="p-4 border-t border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                {userInitial}
              </div>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-gray-800"></div>
            </div>
            {isSidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">
                  {firstName} {lastName}
                </p>
                <p className="text-xs text-gray-400 truncate">{userEmail}</p>
                <div className="flex items-center mt-1">
                  <FiShield className="text-amber-400 mr-1" size={12} />
                  <span className="text-xs text-amber-400 font-medium">Premium</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.aside>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ x: -300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            transition={{ type: "tween" }}
            className="md:hidden fixed inset-y-0 left-0 z-40 w-64 bg-gray-800 shadow-lg"
          >
            <div className="h-full flex flex-col">
              <div className="flex items-center justify-between h-16 px-4 border-b border-gray-700">
                <h1 className="text-xl font-bold text-amber-400">Menu</h1>
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700"
                >
                  <FiX size={24} />
                </button>
              </div>
              <nav className="flex-1 px-2 py-4 space-y-1">
                {navItems.map((item) => {
                  const Icon = item.icon
                  const active = isActive(item.path)
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg ${
                        active ? "bg-amber-500 text-white" : "text-gray-300 hover:bg-gray-700 hover:text-white"
                      }`}
                      onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    >
                      <Icon className="flex-shrink-0 h-5 w-5" />
                      <span className="ml-3">{item.label}</span>
                    </Link>
                  )
                })}
              </nav>
              <div className="p-4 border-t border-gray-700">
                <button
                  onClick={() => {
                    navigate("/login")
                    setIsMobileMenuOpen(false)
                  }}
                  className="w-full flex items-center px-4 py-2 text-sm text-red-400 hover:bg-red-900 hover:bg-opacity-20 rounded-lg"
                >
                  <FiLogOut className="h-5 w-5" />
                  <span className="ml-3">Déconnexion</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Enhanced Main Content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-x-hidden">
        {/* Enhanced Top Navigation — overflow-visible pour ne pas rogner les panneaux */}
        <header className="relative z-30 hidden md:flex items-center justify-between h-16 shrink-0 bg-gray-800 border-b border-gray-700 px-6 shadow-sm overflow-visible">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-white">
              {navItems.find((item) => isActive(item.path))?.label || "Tableau de bord"}
            </h2>
            <div className="flex items-center space-x-2 text-gray-400">
              <FiClock size={16} />
              <span className="text-sm">{new Date().toLocaleDateString("fr-FR")}</span>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <form onSubmit={handleSearch} className="relative hidden lg:block">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Recherche globale..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors"
              />
            </form>

            <Link
              to="/client/search"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all transform hover:scale-105"
            >
              <FiPlus className="-ml-1 mr-2 h-4 w-4" />
              Nouveau devis
            </Link>

            <div className="relative">
              <button
                type="button"
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-gray-400 rounded-lg hover:bg-gray-700 hover:text-white transition-colors"
              >
                <FiBell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-medium text-white bg-amber-500 rounded-full animate-pulse">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              <AnimatePresence>
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    className="absolute top-full right-0 z-[100] mt-2 w-80 rounded-lg shadow-xl ring-1 ring-gray-700"
                  >
                    <NotificationCenter onClose={() => setShowNotifications(false)} variant="dark" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="relative">
              <button
                type="button"
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 p-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-amber-500 to-yellow-600 flex items-center justify-center text-white font-bold text-sm">
                  {userInitial}
                </div>
              </button>
              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    className="origin-top-right absolute top-full right-0 z-[90] mt-2 w-48 rounded-md shadow-lg bg-gray-800 ring-1 ring-gray-700"
                  >
                    <div className="py-1" role="menu" aria-orientation="vertical">
                      <div className="px-4 py-2 border-b border-gray-700">
                        <p className="text-sm font-medium text-white">
                          {firstName} {lastName}
                        </p>
                        <p className="text-xs text-gray-400 truncate">{userEmail}</p>
                      </div>
                      <Link
                        to="/client/profile"
                        className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                        role="menuitem"
                        onClick={() => setShowUserMenu(false)}
                      >
                        Mon profil
                      </Link>
                      <Link
                        to="/client/settings"
                        className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                        role="menuitem"
                        onClick={() => setShowUserMenu(false)}
                      >
                        Paramètres
                      </Link>
                      <button
                        type="button"
                        onClick={() => {
                          localStorage.removeItem("token")
                          localStorage.removeItem("user")
                          setShowUserMenu(false)
                          window.location.href = "/login"
                        }}
                        className="w-full text-left block px-4 py-2 text-sm text-red-400 hover:bg-red-900 hover:bg-opacity-20"
                        role="menuitem"
                      >
                        Déconnexion
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto focus:outline-none p-4 md:p-6 bg-gray-900">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
